import { META_PIXEL_ID } from '../config';

// Meta (Facebook) Pixel loader for the ad-warming pilot. Kept deliberately small:
// it plants the pixel + a single PageView so visitors from the warming ads can be
// retargeted. It is gated by VITE_META_PIXEL_ID (set only in the brand's env, e.g.
// .env.gop for the Republican build), so non-configured builds are a pure no-op.

type FbqFn = ((...args: unknown[]) => void) & {
  queue: unknown[][];
  callMethod?: (...args: unknown[]) => void;
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
    __metaPixelInitialized?: boolean;
  }
}

const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

// Faithful, readable equivalent of Meta's minified bootstrap: create the `fbq`
// stub that queues calls until the real fbevents.js takes over via `callMethod`.
function ensureFbq(): FbqFn {
  if (window.fbq) return window.fbq;
  const fbq = function (this: unknown, ...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  } as FbqFn;
  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.push = fbq;
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  return fbq;
}

function loadFbeventsScript(): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = FBEVENTS_SRC;
  const firstScript = document.getElementsByTagName('script')[0];
  // In a real page main.tsx is itself a <script>, so we insert before it; in tests
  // (or any script-less document) fall back to appending to <head>.
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Initialize the Meta pixel and fire a PageView. No-op (returns false) when no
 * pixel id is configured or it has already been initialized.
 * @returns whether the pixel was initialized on this call.
 */
export function initMetaPixel(
  pixelId: string | undefined = META_PIXEL_ID
): boolean {
  if (!pixelId) return false;
  if (window.__metaPixelInitialized) return false;

  const fbq = ensureFbq();
  loadFbeventsScript();
  fbq('init', pixelId);
  fbq('track', 'PageView');
  window.__metaPixelInitialized = true;
  return true;
}
